/**
 * Safe Storage Service
 * Centralized wrapper around localStorage that:
 * - Surfaces non-critical errors explicitly (returns Result-like tuples)
 * - Validates payloads before read/write
 * - Provides a single audit point for storage behavior
 *
 * Pattern: Adapter / Facade
 * Single Responsibility: encapsulate browser storage access
 */

import type { StorageKey } from './storageKeys'

export type StorageResult<T> =
  | { ok: true; value: T }
  | { ok: false; reason: 'unavailable' | 'parse_error' | 'validation_error' | 'quota_exceeded' }

const isStorageAvailable = (): boolean => {
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
  } catch {
    return false
  }
}

export const safeStorage = {
  readJSON<T>(key: StorageKey, validate: (raw: unknown) => raw is T): StorageResult<T> {
    if (!isStorageAvailable()) return { ok: false, reason: 'unavailable' }

    try {
      const raw = window.localStorage.getItem(key)
      if (raw === null) return { ok: false, reason: 'unavailable' }

      const parsed: unknown = JSON.parse(raw)
      if (!validate(parsed)) return { ok: false, reason: 'validation_error' }

      return { ok: true, value: parsed }
    } catch {
      return { ok: false, reason: 'parse_error' }
    }
  },

  writeJSON(key: StorageKey, value: unknown): StorageResult<void> {
    if (!isStorageAvailable()) return { ok: false, reason: 'unavailable' }

    try {
      window.localStorage.setItem(key, JSON.stringify(value))
      return { ok: true, value: undefined }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        return { ok: false, reason: 'quota_exceeded' }
      }
      return { ok: false, reason: 'unavailable' }
    }
  },

  remove(key: StorageKey): StorageResult<void> {
    if (!isStorageAvailable()) return { ok: false, reason: 'unavailable' }
    try {
      window.localStorage.removeItem(key)
      return { ok: true, value: undefined }
    } catch {
      return { ok: false, reason: 'unavailable' }
    }
  },

  getString(key: StorageKey): StorageResult<string> {
    if (!isStorageAvailable()) return { ok: false, reason: 'unavailable' }
    try {
      const value = window.localStorage.getItem(key)
      if (value === null) return { ok: false, reason: 'unavailable' }
      return { ok: true, value }
    } catch {
      return { ok: false, reason: 'unavailable' }
    }
  },

  setString(key: StorageKey, value: string): StorageResult<void> {
    if (!isStorageAvailable()) return { ok: false, reason: 'unavailable' }
    try {
      window.localStorage.setItem(key, value)
      return { ok: true, value: undefined }
    } catch {
      return { ok: false, reason: 'unavailable' }
    }
  },
}
